import React, { useEffect, useMemo } from "react";
import {
  Button,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
} from "@material-ui/core";

import { isAuthorized } from "../../../utils/appUtils";
import {
  AclType,
  InventoryStatus,
  originDamageList,
} from "../../../utils/constants";
import { handleNumberFieldKeyDown } from "../../../utils/utils";
import { isBulkLocationAvailable } from "../../../utils/locationUtils";

import { useWorkOrderContext } from "./context";
import { useZoneSelector } from "./useZoneSelector";

export const BulkAisleControl = ({
  handleDeleteButtonClick,
  handleAisleChange,
  handleSave,
  classes,
}) => {
  const { state, dispatch } = useWorkOrderContext();
  const {
    currentShipment,
    currentInventory,
    aisles,
    selectedAisle,
    selectedLocation,
    selectedInventoryItem,
    disableSelection,
  } = state;
  const { zones, selectedZone, showZoneSelect, handleZoneChange } =
    useZoneSelector();

  const isContainerDrop = currentShipment?.containerDrop;
  const isNewInventoryItem =
    !selectedInventoryItem?.id ||
    selectedInventoryItem?.status === InventoryStatus.IN_TRANSIT;
  const memoizedSelectedInventoryItem = useMemo(
    () => selectedInventoryItem,
    [selectedInventoryItem],
  );

  const setQuantity = (value) => {
    if (!value) value = 0;
    const quantity = Number(value);
    const newInventoryItem = {
      ...selectedInventoryItem,
      quantityOnHand: quantity,
    };
    dispatch({
      type: "SET_SELECTED_INVENTORY_ITEM",
      payload: newInventoryItem,
    });
  };

  useEffect(() => {
    if (
      memoizedSelectedInventoryItem?.quantityOnHand !==
      currentInventory?.quantityAdvised
    ) {
      dispatch({
        type: "SET_SELECTED_INVENTORY_ITEM",
        payload: {
          ...memoizedSelectedInventoryItem,
          quantityOnHand: currentInventory?.quantityAdvised,
        },
      });
    }
  }, [
    dispatch,
    currentInventory?.quantityAdvised,
    memoizedSelectedInventoryItem,
  ]);

  const doHandleSave = async () => {
    await handleSave({
      isUpdatingShipment: !isNewInventoryItem && isContainerDrop,
    });
  };

  const handleDelete = () => {
    handleDeleteButtonClick();
  };

  const getQuantity = () => {
    return selectedInventoryItem?.quantityOnHand
      ? selectedInventoryItem.quantityOnHand
      : "";
  };

  const isDeleteButtonDisabled = () => {
    if (
      !isAuthorized(AclType.ASSIGN_LOCATION) ||
      !selectedLocation ||
      !selectedInventoryItem?.id ||
      currentInventory.status === InventoryStatus.UNLOADED
    )
      return true;
    return false;
  };

  const isSaveButtonDisabled = useMemo(() => {
    if (isContainerDrop) {
      return false;
    }
    if (
      !selectedAisle ||
      !selectedLocation ||
      !selectedInventoryItem?.quantityOnHand
    ) {
      return true;
    }
    if (selectedInventoryItem?.status === InventoryStatus.IN_TRANSIT) {
      // item is allocated to location, cannot change location, must be able to place
      return false;
    }
    // selectedInventoryItem?.status !== InventoryStatus.IN_TRANSIT &&
    if (!isAuthorized(AclType.ASSIGN_LOCATION)) {
      return true;
    }
    return !isBulkLocationAvailable(
      selectedLocation,
      currentInventory,
      selectedInventoryItem.quantityOnHand,
      selectedInventoryItem.isDamaged,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedLocation?.id,
    selectedInventoryItem?.quantityOnHand,
    selectedInventoryItem?.isDamaged,
  ]);

  const isQuantityDisabled = () => {
    if (isContainerDrop) {
      return true;
    }
    if (
      !selectedAisle ||
      !isAuthorized(AclType.ASSIGN_LOCATION) ||
      currentInventory.status === InventoryStatus.UNLOADED
    ) {
      return true;
    }
    return false;
  };

  const setIsDamaged = (value) => {
    const inventoryItem = selectedInventoryItem
      ? { ...selectedInventoryItem }
      : {};
    if (value) {
      inventoryItem.isDamaged = true;
      inventoryItem.originOfDamage = value;
      inventoryItem.hasException = true;
    } else {
      inventoryItem.isDamaged = false;
      inventoryItem.originOfDamage = "";
      inventoryItem.hasException = false;
    }

    dispatch({ type: "SET_SELECTED_INVENTORY_ITEM", payload: inventoryItem });
  };

  const isAisleDisabled = () => {
    return disableSelection;
  };

  const showRemoveButton = !(
    isContainerDrop && currentInventory?.inventoryItems?.items?.length > 0
  );

  const inputGridSizes = showZoneSelect
    ? { xs: 6, sm: 6, md: 3, lg: 2 }
    : { xs: 12, sm: 4, md: 4, lg: 3 };
  const buttonGridSizes = showRemoveButton
    ? { xs: 6, md: 2, lg: 2 }
    : { xs: 12, md: 2, lg: 2 };

  return (
    <Grid item container xs={12} spacing={2}>
      {showZoneSelect && (
        <Grid item {...inputGridSizes} xs={6}>
          <TextField
            select
            id="zone"
            label="Zone"
            variant="outlined"
            size="small"
            disabled={disableSelection}
            required
            autoFocus
            fullWidth
            value={selectedZone ? selectedZone.id : ""}
            onChange={(evt) => handleZoneChange(evt.target.value)}
          >
            {zones &&
              zones.map((itm) => (
                <MenuItem key={itm.id} value={itm.id}>
                  {itm.name}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
      )}
      <Grid item {...inputGridSizes}>
        <TextField
          select
          id="aisle"
          label="Aisle"
          variant="outlined"
          size="small"
          disabled={isAisleDisabled()}
          required
          autoFocus
          fullWidth
          value={selectedAisle ? selectedAisle.id : ""}
          onChange={(evt) =>
            handleAisleChange(evt.target.value, null, { userInitiated: true })
          }
        >
          {aisles &&
            [{ id: "", value: "", name: <span>&nbsp;</span> }, ...aisles].map(
              (itm) => (
                <MenuItem key={itm.id} value={itm.id}>
                  {itm.name}
                </MenuItem>
              ),
            )}
        </TextField>
      </Grid>
      <Grid item {...inputGridSizes} xs={6}>
        <TextField
          id="quantity-received"
          label="Quantity"
          variant="outlined"
          size="small"
          type="number"
          fullWidth
          inputProps={{ min: 1 }}
          disabled={isQuantityDisabled()}
          value={getQuantity()}
          onChange={(evt) => setQuantity(evt.target.value)}
          onKeyDown={handleNumberFieldKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {currentInventory.inventoryUOM}
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item {...inputGridSizes} xs={6} lg={2}>
        <TextField
          id="damage"
          label="Damaged"
          variant="outlined"
          select
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={
            selectedInventoryItem?.originOfDamage
              ? selectedInventoryItem.originOfDamage
              : ""
          }
          onChange={(evt) => setIsDamaged(evt.target.value)}
          SelectProps={{
            displayEmpty: true,
            renderValue: (value) => {
              if (!value) return "No Damage";
              const matchedDamage = originDamageList.find(
                (item) => item.id === value,
              );
              return matchedDamage?.name ?? value;
            },
          }}
        >
          {[
            { id: "", value: "No Damage", name: <span>No Damage</span> },
            ...originDamageList,
          ].map((itm) => (
            <MenuItem key={itm.id} value={itm.id}>
              {itm.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {showRemoveButton && (
        <Grid item {...buttonGridSizes}>
          <Button
            fullWidth
            variant="contained"
            className={classes.deleteButton}
            onClick={handleDelete}
            disabled={isDeleteButtonDisabled()}
          >
            REMOVE
          </Button>
        </Grid>
      )}

      <Grid item {...buttonGridSizes}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          className={classes.iconButton}
          onClick={() => doHandleSave()}
          disabled={isSaveButtonDisabled}
        >
          {isNewInventoryItem ? "place" : "save"}
        </Button>
      </Grid>
    </Grid>
  );
};
